import { QuestionMarkCircledIcon } from '@radix-ui/react-icons'
// @ts-expect-error: @rails/request.js is not typed
import { FetchRequest } from '@rails/request.js'
import { UserPlusIcon } from 'lucide-react'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Banner as BannerV2, Button, ButtonGroup } from 'slab'

import {
  type EmployeeTableRow,
  type EnrollmentEvent,
  type EnrollmentEventInput,
} from '../types'
import {
  getDateNumDaysFromStartDate,
  getDateString,
  getDateStringNoTimezone,
  getFormattedDate,
  getISODateString,
  getPSTToday,
  getUTCToday,
} from '../utils'
import ConfirmAllowancesDialog from './ConfirmAllowancesDialog'
import InviteEmployeesInstructions from './InviteEmployeesInstructions'
import { Banner } from './reusable/Banner'
import {
  FormDatePickerField,
  FormSelectField,
} from './reusable/form/FormFields'
import OpacityTransition from './reusable/OpacityTransition'
import QuestionMarkTooltip from './reusable/QuestionMarkTooltip'
import SelectedEmployeesList from './SelectedEmployeesList'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet'

type SubmissionError = {
  message: string
  errors?: string[]
}

type EnrollmentEventEventType = {
  type: 'ichra_invite' | 'open_enrollment'
  earliest_invite_date?: string
  latest_enrollment_window_start_date?: string
  latest_enrollment_window_end_date?: string
  force_static_start_date?: boolean
  force_static_end_date?: boolean
  enrollment_window_message?: string
}

type EmployeeInviteOptions = {
  plan_year: number
  event_types: EnrollmentEventEventType[]
}

const denormalizedEventTypes = {
  ichra_invite: 'Initial Benefits Invite',
  open_enrollment: 'Open Enrollment',
}

const calculateEnrollmentWindowLength = (
  startDate: string | undefined,
  endDate: string | undefined
) => {
  // Default to 14 days if either date is missing.
  if (!startDate || !endDate) {
    return 14
  }

  // Calculate the difference in time (milliseconds)
  const diffTime = Math.abs(
    new Date(startDate).getTime() - new Date(endDate).getTime()
  )

  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

const EnrollmentWindowTooltip = () => {
  return (
    <QuestionMarkTooltip>
      <div
        style={{ zIndex: 100, width: '20rem' }}
        className='text-xs p-2 w-60 rounded-md text-center'
      >
        The enrollment window is the number of days the employee has to enroll
        in benefits and complete plan selection with Thatch.
      </div>
    </QuestionMarkTooltip>
  )
}

const EndDateTooltip = () => {
  return (
    <QuestionMarkTooltip>
      <div
        style={{ zIndex: 100, width: '20rem' }}
        className='text-xs p-2 w-60 rounded-md text-center'
      >
        Employees will be able to complete plan selection until midnight PST of the
        end date specified.
      </div>
    </QuestionMarkTooltip>
  )
}

type ChildProps = {
  children?: React.ReactNode
}

export const CreateOrEditEnrollmentEventContext = createContext<{
  isOpen: boolean
  selectedRows: EmployeeTableRow[]
  enrollmentEvent: EnrollmentEvent | undefined
  openCreateOrEditEnrollmentEvent: (
  selectedRows: EmployeeTableRow[],
  enrollmentEvent?: EnrollmentEvent
  ) => void
  closeCreateOrEditEnrollmentEvent: () => void
}>({
      isOpen: false,
      selectedRows: [] as EmployeeTableRow[],
      enrollmentEvent: undefined as EnrollmentEvent | undefined,
      openCreateOrEditEnrollmentEvent: () => {},
      closeCreateOrEditEnrollmentEvent: () => {},
    })

function useCreateOrEditEnrollmentEventProvider () {
  const [isOpen, setIsOpen] = useState(false)
  const [enrollmentEvent, setEnrollmentEvent] = useState<
    EnrollmentEvent | undefined
  >(undefined)
  const [selectedRows, setSelectedRows] = useState<EmployeeTableRow[]>([])

  function openCreateOrEditEnrollmentEvent (
    selectedRows: EmployeeTableRow[],
    enrollmentEvent?: EnrollmentEvent
  ) {
    setSelectedRows(selectedRows)
    setEnrollmentEvent(enrollmentEvent)
    setIsOpen(true)
  }

  function closeCreateOrEditEnrollmentEvent () {
    setSelectedRows([])
    setEnrollmentEvent(undefined)
    setIsOpen(false)
  }

  return {
    isOpen,
    selectedRows,
    enrollmentEvent,
    openCreateOrEditEnrollmentEvent,
    closeCreateOrEditEnrollmentEvent,
  }
}

export function CreateOrEditEnrollmentEventProvider ({ children }: ChildProps) {
  const value = useCreateOrEditEnrollmentEventProvider()

  return (
    <CreateOrEditEnrollmentEventContext.Provider value={value}>
      {children}
    </CreateOrEditEnrollmentEventContext.Provider>
  )
}

export function useCreateOrEditEnrollmentEvent () {
  return useContext(CreateOrEditEnrollmentEventContext)
}

export function determineEndDate (startDate: string, windowLength: string, maxEndDate: Date, forceStaticEndDate?: boolean, staticEndDate?: string) {
  // If force_static_end_date is true, and we have a static end date, use it directly
  if (forceStaticEndDate && staticEndDate) {
    return new Date(staticEndDate)
  }

  const estimatedEndDate = getDateNumDaysFromStartDate(startDate, windowLength)
  if (estimatedEndDate > maxEndDate) {
    return maxEndDate
  }
  return estimatedEndDate
}

export function CreateOrEditEnrollmentEvent ({
  fetchEmployees,
  resetSelectedRowIds,
  setIsEmployeeInviteOpen,
  hasInvitedEmployeesBefore,
  selectedPlanYear,
  employerId,
  apiType,
}: {
  resetSelectedRowIds: () => void
  fetchEmployees: () => void
  setIsEmployeeInviteOpen?: React.Dispatch<React.SetStateAction<boolean>>
  hasInvitedEmployeesBefore: boolean
  selectedPlanYear: number
  employerId?: string,
  apiType?: 'brokers'
}) {
  const {
    isOpen,
    selectedRows,
    enrollmentEvent,
    closeCreateOrEditEnrollmentEvent,
  } = useCreateOrEditEnrollmentEvent()

  const [submissionError, setSubmissionError] = useState<
    SubmissionError | undefined
  >()
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [employeeInviteOptions, setEmployeeInviteOptions] = useState<
    EmployeeInviteOptions[]
  >([])
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false)
  const [isConfirmAllowancesDialogOpen, setIsConfirmAllowancesDialogOpen] =
    useState(!hasInvitedEmployeesBefore)

  const defaultValues = {
    enrollment_type: enrollmentEvent?.enrollment_type,
    enrollment_window_start_date:
      enrollmentEvent?.enrollment_window_start_date ||
      getDateString(getPSTToday()),
    enrollment_window_length_in_days: calculateEnrollmentWindowLength(
      enrollmentEvent?.enrollment_window_start_date,
      enrollmentEvent?.enrollment_window_end_date
    ).toString(),
    plan_year: selectedPlanYear,
  }

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    reset,
  } = useForm<EnrollmentEventInput>({
    defaultValues
  })

  // This hook ensures the employee form is prepopulated with the enrollment events's data when editing an event. This is needed
  // since the form initially mounts with empty values.
  useEffect(() => {
    if (!enrollmentEvent) {
      return
    }

    setValue('enrollment_type', enrollmentEvent?.enrollment_type || '')
    setValue(
      'enrollment_window_start_date',
      enrollmentEvent?.enrollment_window_start_date || ''
    )
    setValue(
      'enrollment_window_length_in_days',
      calculateEnrollmentWindowLength(
        enrollmentEvent?.enrollment_window_start_date,
        enrollmentEvent?.enrollment_window_end_date
      ).toString()
    )
  }, [enrollmentEvent])

  useEffect(() => {
    setValue('plan_year', selectedPlanYear)
  }, [selectedPlanYear])

  const fetchEmployeeInviteOptions = async () => {
    try {
      const url = apiType === 'brokers'
        ? `/api/web/brokers/employers/${employerId}/employees/invite_options`
        : '/api/employees/invite_options'
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })

      if (!response.ok) {
        throw Error('Failed to fetch employee invite options')
      }

      const json = await response.json()
      const { options }: { options: EmployeeInviteOptions[] } = json.data

      setEmployeeInviteOptions(options)
    } catch (e) {
      setSubmissionError({
        message:
          'Failed to load employee invite options. Please try again. If you continue to see this error, please contact support.',
      })
      console.error(e)
    }
  }

  useEffect(() => {
    fetchEmployeeInviteOptions()
  }, [])

  const isCreate = !enrollmentEvent

  const startDate = watch('enrollment_window_start_date')
  const windowLength = watch('enrollment_window_length_in_days')
  const enrollmentType: EnrollmentEventEventType['type'] =
    watch('enrollment_type')

  const planYear = watch('plan_year')

  // Get the current event type configuration
  const inviteOptionsCurrentlySelectedPlanYear = employeeInviteOptions.find(
    (option) => option.plan_year.toString() === planYear.toString()
  )
  const { event_types: eventTypes } =
    inviteOptionsCurrentlySelectedPlanYear || {
      event_types: [] as EnrollmentEventEventType[],
    }
  const currentEventType = eventTypes.find(
    (option) => option.type === enrollmentType
  ) || {
    earliest_invite_date: undefined,
    latest_enrollment_window_start_date: undefined,
    latest_enrollment_window_end_date: undefined,
    force_static_start_date: false,
    force_static_end_date: false,
    enrollment_window_message: undefined
  }

  const {
    earliest_invite_date: earliestInviteDate,
    latest_enrollment_window_start_date: latestEnrollmentWindowStartDate,
    latest_enrollment_window_end_date: latestEnrollmentWindowEndDate,
    force_static_start_date: forceStaticStartDate,
    force_static_end_date: forceStaticEndDate,
    enrollment_window_message: enrollmentWindowMessage,
  } = currentEventType

  // Determine effective start date for fixed windows
  const effectiveStartDate = useMemo(() => {
    const today = getPSTToday()
    const todayString = getDateString(today)

    // Case 1: Fixed start date - use latestEnrollmentWindowStartDate or today (whichever is later)
    if (enrollmentType === 'open_enrollment' && forceStaticStartDate && latestEnrollmentWindowStartDate) {
      const latestStartDate = new Date(latestEnrollmentWindowStartDate)
      return latestStartDate >= today ? latestEnrollmentWindowStartDate : todayString
    }

    // Cases 2-4: Dynamic start date - ensure start date is not before earliestInviteDate
    if (enrollmentType === 'open_enrollment' && !forceStaticStartDate && earliestInviteDate) {
      const userStartDate = startDate ? new Date(startDate) : null
      const earliestDate = new Date(earliestInviteDate)

      // If user has selected a valid start date, use it
      if (userStartDate && userStartDate >= earliestDate) {
        return startDate
      }

      // Otherwise, use earliestInviteDate or today (whichever is later)
      return earliestDate >= today ? earliestInviteDate : todayString
    }

    return startDate
  }, [enrollmentType, forceStaticStartDate, latestEnrollmentWindowStartDate, earliestInviteDate, startDate])

  // Determine max end date based on event type configuration
  const maxEndDate = latestEnrollmentWindowEndDate
    ? new Date(latestEnrollmentWindowEndDate)
    : (enrollmentType === 'open_enrollment'
        ? new Date(Date.UTC(planYear - 1, 11, 15)) // December 15th UTC
        : new Date(Date.UTC(planYear, 10, 30))) // November 30th UTC

  const estimatedEndDate = determineEndDate(startDate, windowLength, maxEndDate, forceStaticEndDate, latestEnrollmentWindowEndDate)

  // Format the end date for display
  const formattedEndDate = forceStaticEndDate && latestEnrollmentWindowEndDate
    ? getFormattedDate(latestEnrollmentWindowEndDate, { timeZone: 'UTC' })
    : getFormattedDate(getDateString(estimatedEndDate), { timeZone: 'UTC' })

  // Check if the estimated end date is using the max end date (window was truncated)
  const isUsingMaxEndDate = estimatedEndDate.getTime() === maxEndDate.getTime() && !forceStaticEndDate

  const isInvalidEndDate =
    enrollmentType === 'open_enrollment' &&
    estimatedEndDate > maxEndDate

  const initialInviteOverlapsWithOEP =
    enrollmentType !== 'open_enrollment' &&
    estimatedEndDate >= maxEndDate &&
    // Only show OEP overlap warning if we're hitting the October 29th limit (new employers)
    getDateStringNoTimezone(maxEndDate) === getDateStringNoTimezone(new Date(planYear, 9, 29))

  // Generic warning if window is shortened due to max end date
  const showWindowShortenedWarning =
    isUsingMaxEndDate &&
    !isInvalidEndDate &&
    !initialInviteOverlapsWithOEP &&
    !enrollmentWindowMessage &&
    !forceStaticEndDate

  // If the start date is before the earliest invite date, set the start date to the earliest invite date.
  if (
    startDate &&
    earliestInviteDate &&
    new Date(startDate) < new Date(earliestInviteDate)
  ) {
    setValue('enrollment_window_start_date', getDateStringNoTimezone(new Date(earliestInviteDate)))
  }

  // Make sure enrollment event and start date are valid options based on new plan year. If not, set valid values.
  if (
    eventTypes.length > 0 &&
    !eventTypes.some((event) => event.type === enrollmentType)
  ) {
    // Set the enrollment type.
    const newType = eventTypes[0]?.type
    setValue('enrollment_type', newType)
    // Set the earliest invite date, if provided and in the future. Otherwise default to today's date.
    const { earliest_invite_date: earliestInviteDate } = eventTypes.find(
      (event) => event.type === newType
    ) || { earliest_invite_date: undefined }
    if (earliestInviteDate) {
      const latestDate =
          new Date(earliestInviteDate).getTime() > getPSTToday().getTime()
            ? earliestInviteDate
            : getDateString(getPSTToday())
      setValue('enrollment_window_start_date', latestDate)
    } else {
      setValue('enrollment_window_start_date', getDateString(getPSTToday()))
    }
  }

  const onSubmit: SubmitHandler<EnrollmentEventInput> = async (data) => {
    window.segment?.track('[ER] Invite employees button clicked', {
      selected_employees_count: selectedRows.length,
      selected_employees: selectedRows.map((employee) => employee.id),
    })

    try {
      setSubmitting(true)
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const request = new FetchRequest(
        isCreate ? 'post' : 'put',
        apiType === 'brokers'
          ? `/api/web/brokers/employers/${employerId}/employees/invite`
          : '/api/employees/invite',
        {
          body: {
            enrollment_event_id: enrollmentEvent?.id,
            employee_ids: selectedRows.map((employee) => employee.id),
            enrollment_type: data.enrollment_type,
            enrollment_window_start_date: data.enrollment_window_start_date,
            enrollment_window_end_date: getISODateString(estimatedEndDate), // remove time + timezone
            plan_year: data.plan_year,
          },
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      )
      const response = await request.perform()
      if (response.ok) {
        // Refetch employees to update the table and close the Sheet component.
        fetchEmployees()
        closeCreateOrEditEnrollmentEvent()

        // Reset the state of the parent component if it's managing the open state.
        if (setIsEmployeeInviteOpen) {
          setIsEmployeeInviteOpen(false)
        }

        // Reset form and selected rows.
        resetSelectedRowIds()
        reset({ ...defaultValues, plan_year: selectedPlanYear })

        // Display success toast.
        const toastMsg = `Employee invite${
          selectedRows.length > 1 ? 's' : ''
        } successfully ${isCreate ? 'created' : 'updated'}`
        toast.success(toastMsg)
      } else if (response.statusCode === 400) {
        const { errors } = await response.json

        const errorMsg = `${
          errors.length === 1 ? 'There was an error' : 'There were errors'
        } ${isCreate ? 'creating' : 'updating'} employee invites:`
        setSubmissionError({ message: errorMsg, errors })
      } else if (response.statusCode === 401 || response.statusCode === 403) {
        const { error } = await response.json
        console.error(
          `Error ${isCreate ? 'creating' : 'updating'} enrollment event${
            !isCreate ? ` ${enrollmentEvent!.id}` : ''
          }: `,
          error,
          response.statusCode
        )

        setSubmissionError({
          message: `There was an error ${isCreate ? 'creating' : 'updating'}
         this enrollment event. ${error}`,
        })
      } else {
        const errorData = await response.json
        throw Error(errorData)
      }
    } catch (error: any) {
      console.error(
        `Error ${isCreate ? 'creating' : 'updating'} enrollment event${
          !isCreate ? ` ${enrollmentEvent!.id}` : ''
        }: `,
        error
      )
      setSubmissionError({
        message: `There was an error ${
          isCreate ? 'creating' : 'updating'
        } this enrollment event. Please contact support.`,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeCreateOrEditEnrollmentEvent()
      reset()
      setSubmissionError(undefined)

      window.segment?.track('[ER] Invite employees form closed', {
        selected_employees_count: selectedRows.length,
        plan_year: selectedPlanYear,
        enrollment_type: enrollmentType,
        enrollment_window_start_date: effectiveStartDate,
        enrollment_window_end_date: estimatedEndDate,
        force_static_start_date: forceStaticStartDate,
      })
    }
  }

  const hasNoAvailableEventTypes = eventTypes.length === 0

  useEffect(() => {
    if (enrollmentType === 'open_enrollment' && forceStaticStartDate) {
      setValue('enrollment_window_start_date', effectiveStartDate)
      setValue('enrollment_window_length_in_days', '30')
    }
  }, [forceStaticStartDate, enrollmentType, effectiveStartDate])

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent aria-describedby={undefined} responsiveLayout className='flex flex-col h-full'>
          <SheetHeader className='flex-shrink-0'>
            <SheetTitle className='pb-4'>
              <span className='heading-lg'>
                {isCreate ? 'Invite employees' : 'Edit employee invite'}
              </span>
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col flex-1 overflow-hidden'>
            {isInstructionsOpen
              ? <InviteEmployeesInstructions onBack={() => setIsInstructionsOpen(false)} />
              : (
                <>
                  <div className='flex-1 overflow-auto'>
                    <OpacityTransition key='invite'>
                      <div
                        className='text-thatch-purple-primary cursor-pointer flex items-center hover:underline'
                        onClick={() => setIsInstructionsOpen(true)}
                      >
                        <QuestionMarkCircledIcon />
                        <span className='ml-2'>How do I setup employee invites?</span>
                      </div>
                      {hasNoAvailableEventTypes && (
                        <div className='mt-4'>
                          <Banner type='warning' titleText={`Invites no longer available for ${selectedPlanYear}`}>
                            Please choose another plan year to continue inviting employees.
                          </Banner>
                        </div>
                      )}
                      {submissionError && (
                        <div
                          className='bg-red-100 border border-red-400 text-red-700 p-2 my-2 rounded relative text-center text-xs'
                          role='alert'
                        >
                          {submissionError.message}
                          {submissionError.errors && (
                            <ul>
                              {submissionError.errors.map((error) => (
                                <li key={error}>- {error}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                      <div className='flex flex-col py-6 gap-y-4'>
                        <div>
                          <div className='label-default font-medium text-ds-text-secondary mb-2'>
                            Plan Year
                          </div>
                          <div className='font-medium mt-2'>{planYear}</div>
                        </div>
                        <FormSelectField<EnrollmentEventInput>
                          disabled={submitting}
                          autoFocus
                          error={errors.enrollment_type}
                          fieldOptions={eventTypes.map((option) => ({
                            label: denormalizedEventTypes[option.type],
                            value: option.type,
                          }))}
                          fieldId='enrollment_type'
                          fieldLabel='Enrollment event'
                          register={register}
                          validationRule={{
                            required: 'Enrollment event is required',
                          }}
                        />
                        <div className='font-semibold flex items-center pt-2'>
                          <span className='mr-1'>Enrollment window</span>
                          <EnrollmentWindowTooltip />
                        </div>
                        {forceStaticStartDate && (
                          <div>
                            <div className='flex items-center label-default font-medium text-ds-text-secondary mb-2'>
                              <span className='mr-1'>Start date</span>
                            </div>
                            <div className='font-medium mt-2'>
                              {getFormattedDate(effectiveStartDate)}
                            </div>
                          </div>
                        )}
                        {!forceStaticStartDate && (
                          <FormDatePickerField<EnrollmentEventInput>
                            disabled={submitting}
                            error={errors.enrollment_window_start_date}
                            fieldId='enrollment_window_start_date'
                            fieldLabel='Start date'
                            watch={watch}
                            selectedDateConversionFn={(selectedDate) =>
                              selectedDate
                                ? new Date(selectedDate)
                                : new Date()}
                            handleSelect={(date: Date | undefined) => {
                            // Convert date to string so the timezone doesn't affect how its passed to the API.
                              if (date) {
                                setValue(
                                  'enrollment_window_start_date',
                                  date.toDateString(),
                                  {
                                    shouldValidate: true,
                                  }
                                )
                              }
                            }}
                            disabledCallback={(date) => {
                            // Disable dates before: 1) the earliest invite date or 2) today's date.
                            // Disable dates after: 1) the latest enrollment window start date (if available) or 2) the end of the plan year.
                              const maxStartDate = latestEnrollmentWindowStartDate
                                ? new Date(latestEnrollmentWindowStartDate)
                                : new Date(planYear, 11, 31)

                              const maxEndDate = latestEnrollmentWindowEndDate
                                ? new Date(latestEnrollmentWindowEndDate)
                                : new Date(planYear, 11, 31)

                              return (
                                date >= maxStartDate ||
                            date >= maxEndDate ||
                            date <
                              (earliestInviteDate &&
                              new Date(earliestInviteDate).getTime() >
                                getUTCToday().getTime()
                                ? new Date(earliestInviteDate)
                                : getUTCToday())
                              )
                            }}
                          />
                        )}
                        {!forceStaticEndDate && (
                          <FormSelectField<EnrollmentEventInput>
                            disabled={submitting}
                            error={errors.enrollment_window_length_in_days}
                            fieldId='enrollment_window_length_in_days'
                            fieldLabel='Enrollment window length'
                            fieldOptions={[
                              { label: '2 weeks', value: '14' },
                              { label: '3 weeks', value: '21' },
                              { label: '30 days', value: '30' },
                              // Do not show 60 days for open enrollment events.
                              ...(enrollmentType === 'open_enrollment'
                                ? []
                                : [{ label: '60 days', value: '60' }]),
                            ]}
                            register={register}
                            validationRule={{
                              required: 'Enrollment window length is required',
                            }}
                          />
                        )}
                        <div>
                          <div className='flex items-center label-default font-medium text-ds-text-secondary mb-2'>
                            <span className='mr-1'>End date</span>
                            <EndDateTooltip />
                          </div>
                          <div className='font-medium mt-2'>
                            {formattedEndDate}
                          </div>
                        </div>
                        {(isInvalidEndDate) && (
                          <Banner type='critical' titleText='Invalid end date'>
                            The end date for the Open Enrollment window cannot be
                            later than {latestEnrollmentWindowEndDate
                            ? getFormattedDate(latestEnrollmentWindowEndDate)
                            : 'December 15th'}. Please update your start date or
                            window length to meet this requirement.
                          </Banner>
                        )}
                        {showWindowShortenedWarning && (
                          <Banner type='warning' titleText='Enrollment window shortened'>
                            <p>
                              Your enrollment window has been shortened to {formattedEndDate} due to the maximum end date limit.
                            </p>
                            <br />
                            <p>
                              The full {windowLength}-day window cannot be accommodated with your current start date.
                            </p>
                          </Banner>
                        )}
                        {forceStaticEndDate && enrollmentWindowMessage && (
                          <div className='mt-6'>
                            <Banner type='info' titleText='Open enrollment window notice'>
                              <p className='whitespace-pre-line'>
                                {enrollmentWindowMessage}
                              </p>
                            </Banner>
                          </div>
                        )}
                        {enrollmentType === 'ichra_invite' && enrollmentWindowMessage && !forceStaticEndDate && isUsingMaxEndDate && (
                          <div className='mt-6'>
                            <Banner type='info' titleText='Coverage start date notice'>
                              <p className='whitespace-pre-line'>
                                {enrollmentWindowMessage}
                              </p>
                            </Banner>
                          </div>
                        )}
                      </div>
                      <SelectedEmployeesList employees={selectedRows} />
                    </OpacityTransition>
                  </div>
                  <footer className='flex-shrink-0 mt-4'>
                    <div className='mb-4'>
                      <BannerV2
                        title='Invited employees may receive enrollment reminders via email and SMS. They can opt out anytime.'
                        type='notice'
                      />
                    </div>
                    <ButtonGroup layout='vertical' size='large'>
                      <Button
                        type='submit'
                        variant='primary'
                        size='large'
                        width='full'
                        disabled={hasNoAvailableEventTypes || isInvalidEndDate || submitting}
                      >
                        <div className='flex'>
                          <UserPlusIcon size={21} className='mr-2' />
                          {enrollmentEvent ? 'Save changes' : 'Invite employees'}
                        </div>
                      </Button>
                    </ButtonGroup>
                  </footer>
                </>
                )}
          </form>
        </SheetContent>
      </Sheet>
      {isOpen && (
        <ConfirmAllowancesDialog
          isDialogOpen={isConfirmAllowancesDialogOpen}
          handleConfirmClick={() => setIsConfirmAllowancesDialogOpen(false)}
          employerId={employerId}
          apiType={apiType}
        />
      )}
    </div>
  )
}
